export type StoredData = Record<string, any>;

export type SetItemOptions = { stayConnected?: boolean };

class WebStorage {
  // eslint-disable-next-line no-useless-constructor
  constructor(private eventId: string) {}

  getLocalStorageKey(): string {
    return `platform-${this.eventId}`;
  }

  getRgpdLocalStorageKey(): string {
    return `rgpd-${this.eventId}`;
  }

  // A value that isn't valid JSON is treated as absent: `value[0] === '{'` alone lets
  // truncated content through, and JSON.parse throws in callers that don't catch.
  // eslint-disable-next-line class-methods-use-this
  private readStoredData(storage: Storage, key: string): StoredData | undefined {
    const value = storage.getItem(key);
    if (!value || value[0] !== '{') return undefined;
    try {
      return JSON.parse(value) as StoredData;
    } catch (e) {
      return undefined;
    }
  }

  // sessionStorage when the user did not check "stay connected", or while impersonating:
  // the session survives a refresh but not the tab being closed.
  private getActiveStorage(): Storage {
    return this.readStoredData(sessionStorage, this.getLocalStorageKey())
      ? sessionStorage
      : localStorage;
  }

  removeItem(): void {
    sessionStorage.removeItem(this.getLocalStorageKey());
    localStorage.removeItem(this.getLocalStorageKey());
  }

  getItem(): StoredData | undefined {
    // sessionStorage wins, but an unreadable entry must not shadow a valid one in
    // localStorage: that would log out a user who did check "stay connected".
    return (
      this.readStoredData(sessionStorage, this.getLocalStorageKey()) ??
      this.readStoredData(localStorage, this.getLocalStorageKey())
    );
  }

  getRgpdRules(): StoredData | undefined {
    return this.readStoredData(localStorage, this.getRgpdLocalStorageKey());
  }

  // stayConnected is only known at login time. When omitted (user update, restore),
  // keep writing to whichever storage already holds the session.
  setItem(value: StoredData, { stayConnected }: SetItemOptions = {}): void {
    const valueToSave = JSON.stringify(value);
    let target: Storage;
    if (stayConnected === undefined) {
      target = this.getActiveStorage();
    } else {
      target = stayConnected ? localStorage : sessionStorage;
      // Drop any leftover entry in the other storage, it would take over on the next read
      const other = stayConnected ? sessionStorage : localStorage;
      other.removeItem(this.getLocalStorageKey());
    }
    target.setItem(this.getLocalStorageKey(), valueToSave);
  }
}


export default WebStorage;
